
import React from 'react';
import { useUnifiedChat } from '@/hooks/useUnifiedChat';
import { useChatGroupSelection } from '@/hooks/useChatGroupSelection';
import { useIsMobile } from '@/hooks/use-mobile';
import UserAuthCheck from './UserAuthCheck';
import DesktopChatLayout from './DesktopChatLayout';
import MobileChatInterface from './MobileChatInterface';

interface UnifiedChatInterfaceProps {
  initialGroupId?: string;
  highlightMessageId?: string;
}

const UnifiedChatInterface = ({ initialGroupId, highlightMessageId }: UnifiedChatInterfaceProps) => {
  const isMobile = useIsMobile();

  console.log('🚀 UNIFIED CHAT INTERFACE LOADING');
  console.log('🚀 Is Mobile:', isMobile);
  console.log('🚀 User Agent:', navigator.userAgent);
  console.log('🚀 Window width:', window.innerWidth);

  // Use mobile interface on mobile devices
  if (isMobile) {
    console.log('📱 Using Mobile Chat Interface');
    return <MobileChatInterface initialGroupId={initialGroupId} highlightMessageId={highlightMessageId} />;
  }

  console.log('🖥️ Using Desktop Chat Interface');

  // Enhanced Desktop Chat Wrapper with better error handling
  const DesktopChatWrapper = () => {
    const { 
      user,
      chatGroups, 
      selectedGroup, 
      messages, 
      sendMessage, 
      selectGroup, 
      canCreateGroup,
      loading
    } = useUnifiedChat();

    // Auto-select group from URL parameter
    React.useEffect(() => {
      if (initialGroupId && chatGroups.length > 0 && !selectedGroup) {
        console.log('🎯 Desktop: Auto-selecting group from URL:', initialGroupId);
        const targetGroup = chatGroups.find(group => group.chat_id === initialGroupId);
        if (targetGroup) {
          selectGroup(targetGroup);
          console.log('✅ Desktop: Auto-selected group:', targetGroup.chat_name);
        } else {
          console.warn('⚠️ Desktop: Group not found:', initialGroupId);
        }
      }
    }, [initialGroupId, chatGroups, selectedGroup, selectGroup]);

    // Handle group selection from notifications with enhanced logging
    useChatGroupSelection({
      onSelectGroup: async (group) => {
        console.log('=== DESKTOP: GROUP SELECTION FROM NOTIFICATION ===');
        console.log('Group to select:', group);
        
        try {
          const fullGroup = {
            ...group,
            updated_at: group.created_at,
            is_active: true
          };
          await selectGroup(fullGroup);
          console.log('✅ Desktop: Group selection completed from notification');
        } catch (error) {
          console.error('❌ Desktop: Error selecting group from notification:', error);
        }
      }
    });

    // Check authentication and loading states
    if (loading || !user) {
      console.log('⏳ Desktop: Waiting for user authentication or loading...');
      return <UserAuthCheck user={user} loading={loading} />;
    }

    console.log('=== DESKTOP CHAT WRAPPER RENDER ===');
    console.log('User:', user.username, 'Type:', user.userType);
    console.log('Groups:', chatGroups.length);
    console.log('Selected Group:', selectedGroup?.chat_name, selectedGroup?.chat_id);
    console.log('Messages:', messages.length);
    
    if (messages.length > 0) {
      console.log('Message details:', messages.slice(0, 3).map(m => ({ 
        id: m.message_id, 
        user: m.user_profile?.username || 'Unknown', 
        message: m.message.substring(0, 50),
        timestamp: m.created_at
      })));
    }

    // Enhanced wrapper with better error boundary
    return (
      <DesktopChatLayout 
        user={user}
        userType={user.userType}
        chatGroups={chatGroups}
        selectedGroup={selectedGroup}
        messages={messages}
        sendMessage={sendMessage}
        selectGroup={selectGroup}
        canCreateGroup={canCreateGroup}
        highlightMessageId={highlightMessageId}
      />
    );
  };

  return <DesktopChatWrapper />;
};

export default UnifiedChatInterface;
